import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import addLogDocument from "../addLogDocument.js";

/**
 * Update a document's field in the database via an API endpoint.
 *
 * @param {object} doc_ref - Firestore document reference.
 * @param {string} field - Field to update.
 * @param {any} value - Value to set for the field.
 * @param {string} prefix - API endpoint prefix.
 * @returns {Promise<string>} - Returns "true" on success or error details on failure.
 */
const updateDocDataFieldApi = async (doc_ref, field, value, prefix) => {
  // Define the endpoint URL
  const doc_path = doc_ref.path;
  const collection = doc_path.split("/")[0];
  const doc_id = doc_path.split("/")[1];
  const token = await getOrRefreshFirebaseToken();
  const url = `${prefix}database/update/${collection}/${doc_id}/update-doc-data-field`;

  // Define the request body
  const body = {
    doc_path: doc_path,
    field: field,
    value: transformObjectReferences(value),
  };

  // Define headers
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  try {
    const start = new Date();
    // Make the POST request
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body), // Encode the body as JSON
    });

    const auth = getAuthCentral();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return "User not logged in";
    }

    const db = getFirestore();
    const userRef = doc(db, "user", currentUser.uid);
    const userSnapshot = await getDoc(checkDB(userRef));

    if (!userSnapshot.exists()) {
      return "User document not found";
    }

    const org_id = userSnapshot.get("org_id");
    const responseText = await response.text();
    const end = new Date();
    const elapsed = end - start;

    // Add log document
    await addLogDocument(
      url,
      prefix,
      userRef,
      org_id,
      response.status.toString(),
      "", // Additional optional fields can be provided here
      "",
      JSON.stringify(body), // Request body as string
      responseText,
      "POST",
      elapsed
    );

    // Check if the request was successful
    if (response.ok) {
      // Handle successful response
      const responseData = JSON.parse(responseText);
      return responseData.status ? "true" : responseData.detail;
    } else {
      console.error(`Failed to update document: ${response.status}`);
      return "";
    }
  } catch (error) {
    console.error(`Exception occurred: ${error.message}`);
    return "";
  }
};

export default updateDocDataFieldApi;

/**
 * Recursively scans an object for Firestore DocumentReferences
 * and transforms them into JSON-compatible formats.
 *
 * @param {any} input - The object or value to check and transform.
 * @param {WeakSet} [visited] - Tracks visited objects to prevent circular references.
 * @returns {any} - A transformed object with DocumentReferences replaced by their JSON-compatible paths.
 */
function transformObjectReferences(input, visited = new WeakSet()) {
  // Prevent circular references
  if (input && typeof input === "object") {
    if (visited.has(input)) {
      return null; // Handle circular references as needed
    }
    visited.add(input);
  }

  if (
    input &&
    typeof input === "object" &&
    (input.constructor?.name === "DocumentReference" ||
      input.type === "document") &&
    (input.path || input._key?.path)
  ) {
    return { documentPath: input.path || input._key.path }; // Safely access the path
  }

  // If it's an array, process each element recursively
  if (Array.isArray(input)) {
    return input.map((item) => transformObjectReferences(item, visited));
  }

  // If it's a plain object, process its keys
  if (input && typeof input === "object") {
    const transformed = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        transformed[key] = transformObjectReferences(input[key], visited);
      }
    }
    return transformed;
  }

  // If it's not a DocumentReference or object/array, return as is
  return input;
}
